import { useMemo, useState } from 'react';

import jsPDF from 'jspdf';

function GlobalSearch({
  projectActors,
  projectAlerts,
  projectSignals,
  projectTimeline
}) {

  const [query, setQuery] = useState('');

  const normalizar = (texto) =>
    (texto || '')
      .toString()
      .toLowerCase()
      .trim();

  const resultados = useMemo(() => {

    if (!query.trim()) {
      return {
        actores: [],
        alertas: [],
        rrss: [],
        timeline: []
      };
    }

    const q = normalizar(query);

    const buscarEnItem = (item, campos) =>
      campos.some((campo) => normalizar(item[campo]).includes(q));

    return {
      actores: projectActors.filter((actor) =>
        buscarEnItem(actor, [
          'nombre', 'tipo', 'estado', 'riesgo',
          'comuna', 'sector', 'influencia', 'observaciones', 'tags'
        ])
      ),
      alertas: projectAlerts.filter((alerta) =>
        buscarEnItem(alerta, [
          'titulo', 'nivel', 'actor', 'descripcion',
          'estado', 'fecha', 'tags'
        ])
      ),
      rrss: projectSignals.filter((signal) =>
        buscarEnItem(signal, [
          'red', 'sentimiento', 'riesgo', 'impacto',
          'tipo', 'actor', 'ubicacion', 'texto', 'fecha', 'tags'
        ])
      ),
      timeline: projectTimeline.filter((evento) =>
        buscarEnItem(evento, [
          'titulo', 'fecha', 'tipo', 'actor', 'descripcion'
        ])
      )
    };

  }, [query, projectActors, projectAlerts, projectSignals, projectTimeline]);

  const total =
    resultados.actores.length +
    resultados.alertas.length +
    resultados.rrss.length +
    resultados.timeline.length;

  const exportarPDF = () => {

    const pdf = new jsPDF();

    let y = 20;

    pdf.setFillColor(11, 31, 46);

    pdf.rect(
      0,
      0,
      210,
      40,
      'F'
    );

    pdf.setTextColor(255, 255, 255);

    pdf.setFontSize(30);

    pdf.text(
      'VITA',
      20,
      24
    );

    pdf.setFontSize(12);

    pdf.text(
      'Territorial Intelligence',
      20,
      33
    );

    y = 55;

    pdf.setTextColor(0, 0, 0);

    pdf.setFontSize(22);

    pdf.text(
      'Reporte de Búsqueda Territorial',
      20,
      y
    );

    y += 14;

    pdf.setFontSize(12);

    pdf.text(
      `Consulta ejecutada: ${query}`,
      20,
      y
    );

    y += 8;

    pdf.text(
      `Resultados encontrados: ${total}`,
      20,
      y
    );

    y += 16;

    pdf.setDrawColor(220);

    pdf.line(
      20,
      y,
      190,
      y
    );

    y += 12;

    pdf.setFontSize(14);

    pdf.text(
      'Resumen Ejecutivo',
      20,
      y
    );

    y += 10;

    pdf.setFontSize(11);

    const resumen = `
La búsqueda territorial identificó ${total} resultados relacionados con "${query}".

Stakeholders encontrados: ${resultados.actores.length}
Alertas territoriales: ${resultados.alertas.length}
Señales RRSS: ${resultados.rrss.length}
Eventos timeline: ${resultados.timeline.length}

La consulta permite detectar relaciones territoriales, actores críticos,
riesgos narrativos y evolución de conflictos asociados al territorio analizado.
`;

    const resumenLineas =
      pdf.splitTextToSize(
        resumen,
        170
      );

    pdf.text(
      resumenLineas,
      20,
      y
    );

    y +=
      resumenLineas.length * 6 + 12;

    const agregarSeccion = (
      titulo,
      items,
      render
    ) => {

      if (items.length === 0) return;

      if (y > 230) {

        pdf.addPage();

        y = 20;

      }

      pdf.setFillColor(
        31,
        143,
        95
      );

      pdf.rect(
        20,
        y - 6,
        170,
        10,
        'F'
      );

      pdf.setTextColor(
        255,
        255,
        255
      );

      pdf.setFontSize(13);

      pdf.text(
        titulo,
        24,
        y
      );

      y += 12;

      pdf.setTextColor(
        0,
        0,
        0
      );

      pdf.setFontSize(10);

      items.forEach((item) => {

        const texto =
          render(item);

        const lineas =
          pdf.splitTextToSize(
            texto,
            165
          );

        pdf.text(
          lineas,
          24,
          y
        );

        y +=
          lineas.length * 5 + 8;

        pdf.setDrawColor(230);

        pdf.line(
          24,
          y - 3,
          185,
          y - 3
        );

        if (y > 260) {

          pdf.addPage();

          y = 20;

        }

      });

      y += 8;

    };

    agregarSeccion(
      'Stakeholders',
      resultados.actores,
      (actor) => `
Nombre: ${actor.nombre}
Estado: ${actor.estado}
Riesgo: ${actor.riesgo}
Comuna: ${actor.comuna}
Influencia: ${actor.influencia}
Tags: ${actor.tags || '-'}
`
    );

    agregarSeccion(
      'Alertas',
      resultados.alertas,
      (alerta) => `
Titulo: ${alerta.titulo}
Nivel: ${alerta.nivel}
Estado: ${alerta.estado}
Actor: ${alerta.actor || '-'}
Tags: ${alerta.tags || '-'}
`
    );

    agregarSeccion(
      'RRSS',
      resultados.rrss,
      (signal) => `
Red: ${signal.red}
Sentimiento: ${signal.sentimiento}
Riesgo: ${signal.riesgo}
Ubicacion: ${signal.ubicacion}
Tags: ${signal.tags || '-'}
`
    );

    agregarSeccion(
      'Timeline',
      resultados.timeline,
      (evento) => `
Fecha: ${evento.fecha}
Titulo: ${evento.titulo}
Tipo: ${evento.tipo}
Actor: ${evento.actor || '-'}
`
    );

    pdf.save(
      `VITA_${query}.pdf`
    );

  };

  return (

    <div>

      <div style={styles.header}>

        <h1 style={styles.title}>
          Búsqueda Territorial Global
        </h1>

        <p style={styles.subtitle}>
          Consulta territorial semántica integrada
        </p>

      </div>

      <div style={styles.searchCard}>

        <input
          style={styles.searchInput}
          placeholder="Buscar: agua, APR, Catemu, movilización..."
          value={query}
          onChange={(e) =>
            setQuery(e.target.value)
          }
        />

        {query && (

          <button
            style={styles.clearButton}
            onClick={() =>
              setQuery('')
            }
          >

            Limpiar

          </button>

        )}

      </div>

      {query && (

        <div style={styles.summary}>

          <div>

            Resultados encontrados:
            {' '}
            <strong>
              {total}
            </strong>

          </div>

          <button
            style={styles.exportButton}
            onClick={exportarPDF}
          >

            Exportar PDF

          </button>

        </div>

      )}

      {query && (

        <div style={styles.grid}>

          <ResultSection
            title="Stakeholders"
            items={resultados.actores}

            render={(actor) => (

              <>

                <strong>
                  {actor.nombre}
                </strong>

                <br />

                Estado:
                {' '}
                {actor.estado}

                {' · '}

                Riesgo:
                {' '}
                {actor.riesgo}

                <br />

                Comuna:
                {' '}
                {actor.comuna}

                <br />

                Tags:
                {' '}
                {actor.tags || '-'}

              </>

            )}
          />

          <ResultSection
            title="Alertas"
            items={resultados.alertas}

            render={(alerta) => (

              <>

                <strong>
                  ⚠️ {alerta.titulo}
                </strong>

                <br />

                Nivel:
                {' '}
                {alerta.nivel}

                {' · '}

                Estado:
                {' '}
                {alerta.estado}

                <br />

                Tags:
                {' '}
                {alerta.tags || '-'}

              </>

            )}
          />

          <ResultSection
            title="RRSS"
            items={resultados.rrss}

            render={(signal) => (

              <>

                <strong>
                  {signal.red}
                </strong>

                {' · '}

                {signal.sentimiento}

                <br />

                Riesgo:
                {' '}
                {signal.riesgo}

                <br />

                Tags:
                {' '}
                {signal.tags || '-'}

              </>

            )}
          />

          <ResultSection
            title="Timeline"
            items={resultados.timeline}

            render={(evento) => (

              <>

                <strong>
                  {evento.fecha}
                </strong>

                <br />

                {evento.titulo}

                <br />

                Tipo:
                {' '}
                {evento.tipo}

              </>

            )}
          />

        </div>

      )}

    </div>

  );

}

function ResultSection({
  title,
  items,
  render
}) {

  return (

    <div style={styles.panel}>

      <h2>
        {title}
      </h2>

      {items.length === 0 && (

        <p style={styles.empty}>
          Sin resultados.
        </p>

      )}

      {items.map((item) => (

        <div
          key={item.id}
          style={styles.resultItem}
        >

          {render(item)}

        </div>

      ))}

    </div>

  );

}

const styles = {

  header: {
    marginBottom: '25px'
  },

  title: {
    margin: 0,

    fontSize: '34px',

    color: '#0b1f2e'
  },

  subtitle: {
    color: '#667085'
  },

  searchCard: {
    background: 'white',

    padding: '24px',

    borderRadius: '14px',

    display: 'flex',

    gap: '12px',

    marginBottom: '20px',

    boxShadow:
      '0 8px 25px rgba(0,0,0,0.06)'
  },

  searchInput: {
    flex: 1,

    padding: '16px',

    borderRadius: '12px',

    border: '1px solid #d0d5dd',

    fontSize: '16px'
  },

  clearButton: {
    padding: '0 20px',

    border: 'none',

    borderRadius: '12px',

    background: '#0b1f2e',

    color: 'white',

    cursor: 'pointer'
  },

  exportButton: {
    border: 'none',

    background: '#1f8f5f',

    color: 'white',

    padding: '10px 18px',

    borderRadius: '10px',

    cursor: 'pointer',

    fontWeight: 'bold'
  },

  summary: {
    background: '#eef4ff',

    color: '#1849a9',

    padding: '14px 18px',

    borderRadius: '12px',

    marginBottom: '20px',

    display: 'flex',

    justifyContent: 'space-between',

    alignItems: 'center'
  },

  grid: {
    display: 'grid',

    gridTemplateColumns:
      'repeat(auto-fit,minmax(330px,1fr))',

    gap: '22px'
  },

  panel: {
    background: 'white',

    padding: '24px',

    borderRadius: '14px',

    boxShadow:
      '0 8px 25px rgba(0,0,0,0.06)'
  },

  resultItem: {
    padding: '14px',

    borderBottom:
      '1px solid #eaecf0',

    lineHeight: '1.6'
  },

  empty: {
    color: '#667085'
  }

};

export default GlobalSearch;